import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { Grid, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import { useTranslation } from 'react-i18next';
import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import routes from '@/routes';
import { IconBuilding } from '@tabler/icons-react';
import { IMaskInput } from 'react-imask';
import { formatNumberWithCommas } from '@/helpers/string';
import { getMutationResult } from '@/helpers/query';
import { useSaveMutation } from '@/services/api';
import { notifications } from '@mantine/notifications';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import ProcessButton from '@/components/ProcessButton';

const AssetValue = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { extraDetails, id } = useSelector((state) => state.proposal);
  const [apiCreateOrUpdateOffer] = useSaveMutation();

  const { propertyValuation, isRecycling } = extraDetails;

  const required = t('globals.validations.required');
  const numbers = t('globals.validations.number');
  const minValue = t('globals.validations.minPropertyValue');
  useIdInStoreGuard();

  const formValidations = {
    propertyValuation: (value) => {
      if (!value) return required;
      // Remove commas and check if it's a valid number
      const cleanValue = value.replace(/,/g, '');
      if (!/^\d+$/.test(cleanValue)) return numbers;
      // Check if value is less than 400,000
      const numValue = Number(cleanValue);
      if (numValue < 400000) return minValue;
      return null;
    },
  };

  const initialValues = {
    propertyValuation: formatNumberWithCommas(propertyValuation),
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues,
    validate: formValidations,
    validateInputOnSubmit: true,
  });

  // adding comma to the number input
  const handleNumberChange = (field) => (event) => {
    const { value } = event.target;
    const formattedValue = formatNumberWithCommas(value);
    form.setFieldValue(field, formattedValue);
  };

  const handleContinue = async () => {
    try {
      setLoading(true);

      // Validate form
      const validation = form.validate();
      if (validation.hasErrors) {
        return;
      }

      const cleanValue = form.values.propertyValuation.replace(/,/g, '');
      const numericValue = Number(cleanValue);

      // Keep the value as a string for display purposes
      const formData = {
        ...form.values,
        propertyValuation: numericValue, // Keep as number
        id,
      };

      const {
        isError,
      } = await getMutationResult(formData, apiCreateOrUpdateOffer);

      if (!isError) {
        // Save data
        dispatch(updateExtraDetails({ propertyValuation: numericValue }));

        // Navigate to next step
        navigate(routeWithParams(getRouteById(routes.newProposal.id, 'bankingConduct').path, { id }));
      } else {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }
    } catch (error) {
      console.error('Error saving asset value data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    if (isRecycling === 'yes') {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'existingMortgage').path, { id }));
    } else {
      navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reason').path, { id }));
    }
  };

  return (
    <ProcessHeader
      headerText="שווי הנכס הנרכש"
      headerColor="#D4FCF3"
      headerTextColor="#00ABAB"
      subtitle="נא לציין את שווי הנכס הנרכש"
      infoText="מומלץ לרשום על פי הערכת שמאי או חוזה מכר, הנמוך מביניהם."
      showReturnButton
      onReturn={handleReturn}
      icon={IconBuilding}
    >
      <form onSubmit={form.onSubmit(handleContinue)}>
        <Page.Section>
          <Grid>
            <Grid.Col span={{ base: 12, lg: 5 }}>
              <TextInput
                withAsterisk
                label="שווי הנכס הנרכש (בשקלים ₪)"
                placeholder="הזינו את שווי הנכס"
                key={form.key('propertyValuation')}
                {...form.getInputProps('propertyValuation')}
                onChange={handleNumberChange('propertyValuation')}
                maxLength={15}
                component={IMaskInput}
                scale={0}
                min={0}
              />
            </Grid.Col>
          </Grid>
        </Page.Section>

        <Page.Actions>
          <ProcessButton type="submit" loading={loading} text="המשך לפרטי התנהלות בנקאית" />
        </Page.Actions>
      </form>
    </ProcessHeader>
  );
};

export default AssetValue;
